/**
 * Game Summary / Stat Tracker → site-wide table export sources.
 *
 * Both pages are sheets, not grids, so the file is a tidy long table that
 * reads top-to-bottom in the same order as the screen:
 *
 *   Game Summary: line score (Team / Opponent per quarter, total, result),
 *   clock / quarter / possession, the drive-by-drive scoring summary (with the
 *   page's drive filters applied), then Offense, Defense and Special Teams stat
 *   values — labels and number formatting exactly as displayed.
 *
 *   Stat Tracker: one row per stat of the selected unit, in the sheet's
 *   current search / category / sort, then the custom fields; per-game values
 *   in the season rollup.
 */
import type {
  GameSummaryFeed,
  ScoringDrive,
  StatUnit,
  StatValueFormat,
  TeamEvent,
} from '../../lib/api-types';
import type { ExportCell, ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fileDay } from '../integrations/export-format';
import type { StatSheetRow } from '../tracker/use-tracker';

import { filterDrives, quarterLabel, yardLineLabel, type DriveFilters } from './scoring-summary';
import { formatStatValue, resolveStatRows, toStatListEntries } from './stat-block';

const UNIT_LABELS: Readonly<Record<StatUnit, string>> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

const RESULT_TEXT: Readonly<Record<string, string>> = { win: 'Win', loss: 'Loss', tie: 'Tie' };

/** A stat value as the page shows it (lists spelled out: "42 yd ✓ Q2, 38 yd ✗"). */
export function exportStatValue(value: unknown, format: StatValueFormat | string): ExportCell {
  if (format === 'list' || Array.isArray(value)) {
    const entries = toStatListEntries(value);
    if (entries.length === 0) return 'None';
    return entries
      .map((entry) =>
        [
          entry.label,
          entry.isSuccess === null ? null : entry.isSuccess ? 'made' : 'missed',
          entry.note,
        ]
          .filter((part) => part !== null && part !== '')
          .join(' ')
      )
      .join('; ');
  }
  if (typeof value === 'number' && (format === 'count' || format === 'yards')) {
    return Number.isFinite(value) ? value : null;
  }
  const text = formatStatValue(value, format as StatValueFormat);
  return text === '—' ? null : text;
}

const SUMMARY_COLUMNS: readonly ExportColumnDef[] = [
  { field: 'section', label: 'Section', isVisible: true },
  { field: 'category', label: 'Category', isVisible: true },
  { field: 'item', label: 'Item', isVisible: true },
  { field: 'team', label: 'Team', isVisible: true },
  { field: 'opponent', label: 'Opponent', isVisible: true },
  { field: 'value', label: 'Value', isVisible: true },
  { field: 'detail', label: 'Detail', isVisible: true },
];

function driveDetail(drive: ScoringDrive): string {
  return [
    quarterLabel(drive.quarter),
    `start ${yardLineLabel(drive.start_yard_line)}`,
    `${drive.plays} ${drive.plays === 1 ? 'play' : 'plays'}`,
    `${drive.yards} yds`,
    drive.time_of_possession === null ? null : `TOP ${drive.time_of_possession}`,
    drive.points > 0 ? `${drive.points} pts` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

export function gameSummaryRows(input: {
  feed: GameSummaryFeed;
  teamName: string;
  opponentName: string;
  driveFilters: DriveFilters;
  isSummaryOnly: boolean;
}): ExportCell[][] {
  const { feed, teamName, opponentName } = input;
  const general = feed.statistics.general;
  const rows: ExportCell[][] = [];
  const push = (
    section: string,
    category: string,
    item: string,
    team: ExportCell,
    opponent: ExportCell,
    value: ExportCell,
    detail: ExportCell
  ): void => {
    rows.push([section, category, item, team, opponent, value, detail]);
  };

  push(
    'General',
    'Teams',
    'Team names',
    teamName,
    opponentName,
    null,
    general.is_home ? 'Home' : 'Away'
  );
  for (const quarter of general.score_by_quarter) {
    push(
      'General',
      'Score',
      quarterLabel(quarter.quarter),
      quarter.team_score,
      quarter.opponent_score,
      null,
      null
    );
  }
  push(
    'General',
    'Score',
    'Total',
    general.total_score.team,
    general.total_score.opponent,
    null,
    null
  );
  push(
    'General',
    'Score',
    'Result',
    null,
    null,
    general.result === null ? 'In progress' : (RESULT_TEXT[general.result] ?? general.result),
    null
  );
  push('General', 'Game state', 'Quarter', null, null, quarterLabel(general.quarter), null);
  push('General', 'Game state', 'Game clock', null, null, general.game_clock, null);
  push(
    'General',
    'Game state',
    'Possession',
    null,
    null,
    general.possession === null ? null : UNIT_LABELS[general.possession],
    null
  );

  for (const drive of filterDrives(feed.statistics.scoring_summary, input.driveFilters)) {
    push(
      'Scoring Summary',
      drive.possession === 'team' ? teamName : opponentName,
      `Drive ${drive.drive}`,
      drive.scored_by === 'team' ? drive.points : null,
      drive.scored_by === 'opponent' ? drive.points : null,
      drive.result,
      driveDetail(drive)
    );
  }

  for (const unit of ['offense', 'defense', 'special_teams'] as const) {
    const stats = feed.statistics[unit];
    if (stats === undefined) continue;
    for (const row of resolveStatRows(unit, stats, input.isSummaryOnly)) {
      push(
        UNIT_LABELS[unit],
        row.category,
        row.label,
        null,
        null,
        exportStatValue(stats[row.key], row.format),
        null
      );
    }
  }
  return rows;
}

export function buildGameSummaryExportSource(input: {
  feed: GameSummaryFeed;
  event: TeamEvent | null;
  teamName: string;
  opponentName: string;
  driveFilters: DriveFilters;
  isSummaryOnly: boolean;
}): TableExportSource {
  return {
    page: 'game_summary',
    pageLabel: 'Game Summary',
    columns: SUMMARY_COLUMNS,
    rows: () => gameSummaryRows(input),
    fileStem: exportFileStem(
      'GameSummary',
      `${input.teamName} vs ${input.opponentName}`,
      fileDay(input.event?.scheduled_at ?? null)
    ),
    sheetName: 'Game Summary',
    rowNoun: { one: 'line', many: 'lines' },
  };
}

/**
 * Stat Tracker: the sheet as displayed — built-in stats in the table's current
 * search / category / sort, then the Custom fields block — one row per stat.
 */
export function statTrackerRows(rows: readonly StatSheetRow[], unit: StatUnit): ExportCell[][] {
  const builtIn = rows.filter((row) => !row.isCustom);
  const custom = rows.filter((row) => row.isCustom);
  return [...builtIn, ...custom].map((row): ExportCell[] => [
    UNIT_LABELS[unit],
    row.category,
    row.name,
    exportStatValue(row.value, row.format),
    row.perGame === null ? null : Number(row.perGame.toFixed(1)),
    row.isCustom ? 'Yes' : 'No',
  ]);
}

export function buildStatTrackerExportSource(input: {
  rows: readonly StatSheetRow[];
  unit: StatUnit;
  event: TeamEvent | null;
  view: 'game' | 'season';
  season: number;
}): TableExportSource {
  const columns: ExportColumnDef[] = [
    { field: 'unit', label: 'Unit', header: 'Unit (O/D/ST)', isVisible: true },
    { field: 'category', label: 'Category', isVisible: true },
    { field: 'stat', label: 'Stat', isVisible: true },
    { field: 'value', label: 'Value', isVisible: true },
    { field: 'per_game', label: 'Per Game', isVisible: input.view === 'season' },
    { field: 'is_custom', label: 'Custom stat', isVisible: true },
  ];
  const subject = input.view === 'season' ? `${input.season}_season` : (input.event?.name ?? null);
  return {
    page: 'stat_tracker',
    pageLabel: 'Stat Tracker',
    columns,
    rows: () => statTrackerRows(input.rows, input.unit),
    fileStem: exportFileStem(
      `StatTracker_${UNIT_LABELS[input.unit].replace(/\s+/g, '')}`,
      subject,
      fileDay(input.view === 'season' ? null : (input.event?.scheduled_at ?? null))
    ),
    sheetName: 'Stat Tracker',
    rowNoun: { one: 'stat', many: 'stats' },
  };
}
